import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { IntroData } from 'app/ngrx/intros';
import { ENTITLEMENTS, EntitlementsService } from 'app/services/entitlements.service';
import { LiveUpdateService } from 'app/services/live-update.service';
import { ProTrialService } from 'app/services/pro-trial.service';
import { BehaviorSubject, combineLatest, of, Subject, timer } from 'rxjs';
import { distinctUntilChanged, filter, map, pluck, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { UNANIMATED_CONTAINER } from '../consent-menu-overlay';
import { CreatePassDialogData } from '../create-hallpass-forms/create-hallpass-forms.component';
import { MainHallPassFormComponent, Navigation } from '../create-hallpass-forms/main-hallpass--form/main-hall-pass-form.component';
import { GroupsSettingsComponent } from '../create-hallpass-forms/main-hallpass--form/student-groups/groups-settings/groups-settings.component';
import { DarkThemeSwitch } from '../dark-theme-switch';
import { DeviceDetection } from '../device-detection.helper';
import { FavoriteFormComponent } from '../favorite-form/favorite-form.component';
import { IdcardOverlayContainerComponent } from '../idcard-overlay-container/idcard-overlay-container.component';
import { KioskCheckInDialogComponent } from '../kiosk-mode/kiosk-check-in-dialog/kiosk-check-in-dialog.component';
import { LiveDataService } from '../live-data/live-data.service';
import { NavbarDataService } from '../main/navbar-data.service';
import { HallPass, Location, NoFlyTime, PassLike, PassLimit, Pinnable, Request, User } from '../models';
import { MyProfileDialogComponent } from '../my-profile-dialog/my-profile-dialog.component';
import { NotificationFormComponent } from '../notification-form/notification-form.component';
import { PassCardComponent } from '../pass-card/pass-card.component';
import { ScrollPositionService } from '../scroll-position.service';
import { DataService } from '../services/data-service';
import { EmergencyService } from '../services/emergency.service';
import { FeatureFlagService, FLAGS } from '../services/feature-flag.service';
import { HallPassesService, MODAL_MAX_HEIGHT } from '../services/hall-passes.service';
import { HelpCenterService } from '../services/help-center.service';
import { HttpService } from '../services/http-service';
import { IDCard, IDCardDetails, IDCardResponse, IDCardService } from '../services/IDCardService';
import { KeyboardShortcutsService } from '../services/keyboard-shortcuts.service';
import { KioskModeService } from '../services/kiosk-mode.service';
import { LocationsService } from '../services/locations.service';
import { NavbarElementsRefsService } from '../services/navbar-elements-refs.service';
import { NoFlyTimeService } from '../services/no-fly-time.service';
import { NotificationService } from '../services/notification-service';
import { QRBarcodeGeneratorService } from '../services/qrbarcode-generator.service';
import { ScreenService } from '../services/screen.service';
import { SideNavService } from '../services/side-nav.service';
import { StorageService } from '../services/storage.service';
import { UserService } from '../services/user.service';
import { DialogData, SettingAction, SettingsComponent } from '../settings/settings.component';
import { SmartpassSearchComponent } from '../smartpass-search/smartpass-search.component';
import { SpAppearanceComponent } from '../sp-appearance/sp-appearance.component';
import { SpLanguageComponent } from '../sp-language/sp-language.component';
import { TeacherPinComponent } from '../teacher-pin/teacher-pin.component';

export interface RepresentedUser {
	user: User;
	roles: string[];
}

@Component({
	selector: 'app-navbar',
	templateUrl: './navbar.component.html',
	styleUrls: ['./navbar.component.scss'],
	providers: [DatePipe],
})
export class NavbarComponent implements AfterViewInit, OnInit, OnDestroy {
	@ViewChild('navbar') navbar: ElementRef | undefined;
	@ViewChild('setButton') settingsButton: ElementRef | undefined;
	@ViewChild('passButtonsContainer') passButtonsContainer: ElementRef<HTMLDivElement> | undefined;

	private destroyer$ = new Subject<void>();
	loaded = false;

	/*******************************************************
	 **************** Left Side / Search *******************
	 *******************************************************/

	// FIXME: This hides the search box for non-teachers. It should be handled elsewhere
	@ViewChild('smartpassSearch') set spSearch(comp: SmartpassSearchComponent) {
		if (!comp) {
			return;
		}
		if (!this.user.isTeacher()) {
			const spSearchContainer = document.querySelector<HTMLDivElement>('app-smartpass-search div.input-container');
			if (spSearchContainer) {
				spSearchContainer.style.display = 'none';
			}
		}
	}

	/*******************************************************
	 **************** Center / Tab Navigation **************
	 *******************************************************/

	updateTab(route: string): void {
		this.router.navigateByUrl('/main/' + route);
	}

	/*******************************************************
	 **************** Right Side / Actions *****************
	 *******************************************************/

	/**************
	 ********* Create Pass
	 **************/

	private createHallPassDialogRef: MatDialogRef<MainHallPassFormComponent> | undefined;

	createButtonTooltipText = '';

	// setting this to true by default since we want to disable the button before actual data comes in
	isCreatePassButtonDisabled$ = new BehaviorSubject<boolean>(true);

	// FIXME: Passed to the PassCardComponent, but it looks unused.
	heartBeat = new Subject<number>();

	showMainFormForState(state: Navigation): void {
		if (!this.navbar || !this.passButtonsContainer) {
			console.warn('Attempted to open hall pass dialog before navbar was initialized');
			return;
		}
		const barCoords = this.navbar.nativeElement.getBoundingClientRect();
		const passButtonContainerCoords = this.passButtonsContainer.nativeElement.getBoundingClientRect();
		const dialogTop = `${barCoords.bottom - 5}px`;
		const dialogRight = `${window.innerWidth - passButtonContainerCoords.right + 40}px`;

		this.createHallPassDialogRef?.close();
		this.createHallPassDialogRef = this.dialog.open(MainHallPassFormComponent, {
			hasBackdrop: false,
			closeOnNavigation: true,
			panelClass: 'main-form-dialog-container',
			backdropClass: 'cdk-overlay-transparent-backdrop',
			maxWidth: '100vw',
			position: {
				top: dialogTop,
				right: dialogRight,
			},
			data: {
				FORM_STATE: state,
				forStaff: this.isStaff || this.isAssistant,
				openedFrom: 'navbar',
			} as Partial<CreatePassDialogData>,
		});

		this.createHallPassDialogRef
			.afterClosed()
			.toPromise()
			.then((createdPasses) => {
				if (createdPasses && Array.isArray(createdPasses) && createdPasses?.length > 0) {
					const newPass = createdPasses[0];
					if ((this.isStaff || this.isAssistant) && newPass instanceof HallPass && this.router.url === '/main/passes') {
						this.scrollService.triggerScrollToTop();
					}
				}
			});
	}

	async showMainForm(forLater: boolean): Promise<void> {
		const data: Partial<CreatePassDialogData> = {
			forLater: forLater,
			forStaff: this.isStaff || this.isAssistant,
			forInput: true,
			noFlyTimes: this.noFlyTimes,
			currentNoFlyTimeIndex: this.currentNoFlyTimeIndex,
			noFlyTimeActive: this.noFlyTimeActive,
			currentNoFlyEndTimeString: this.currentNoFlyEndTimeString,
			openedFrom: 'navbar',
			adminSelectedStudent: this.hallPassService.preselectedStudent$.getValue(),
		};

		this.openCreateHallPassDialog(data, forLater);
	}

	private openCreateHallPassDialog(data: Partial<CreatePassDialogData>, forLater: boolean): void {
		if (!this.navbar || !this.passButtonsContainer) {
			console.warn('Attempted to open hall pass dialog before navbar was initialized');
			return;
		}
		const barCoords = this.navbar.nativeElement.getBoundingClientRect();
		const passButtonContainerCoords = this.passButtonsContainer.nativeElement.getBoundingClientRect();
		const dialogTop = `${barCoords.bottom - 5}px`;
		const dialogRight = `${window.innerWidth - passButtonContainerCoords.right + 40}px`;
		this.createHallPassDialogRef = this.dialog.open(MainHallPassFormComponent, {
			hasBackdrop: false,
			closeOnNavigation: true,
			panelClass: 'main-form-dialog-container',
			backdropClass: 'cdk-overlay-transparent-backdrop',
			maxWidth: '100vw',
			position: {
				top: dialogTop,
				right: dialogRight,
			},
			data: data,
		});
		this.createHallPassDialogRef
			.afterClosed()
			.toPromise()
			.then((createdPasses) => {
				if (createdPasses && Array.isArray(createdPasses) && createdPasses?.length > 0) {
					const newPass = createdPasses[0];
					if (this.isStudent && !forLater && !(newPass instanceof HallPass)) {
						this.openStudentPassRequestModal(newPass);
					}

					//scroll to top of the page after creating pass if staff
					if ((this.isStaff || this.isAssistant) && newPass instanceof HallPass && this.router.url === '/main/passes') {
						this.scrollService.triggerScrollToTop();
					}
				}
			});
	}

	openStudentPassRequestModal(newPass: PassLike): void {
		const dialogData = {
			pass: newPass,
			fromPast: false,
			forFuture: false,
			isActive: true,
			forStaff: false,
			kioskMode: false,
			activePassTime$: this.heartBeat,
			showStudentInfoBlock: false,
			hideReport: true,
			openRequestPin: false,
		};
		const passCardComp = this.hallPassService.getPassCardComponent(newPass);

		const modalHeight = this.hallPassService.getModalHeight(newPass, this.isStaff, this.isKioskMode);

		const modalWidth = this.hallPassService.getModalWidth(this.isSmartphone);
		const modalMinWidth = this.hallPassService.getModalMinWidth(this.isSmartphone);
		this.dialog
			.open(passCardComp, {
				panelClass: 'student-pass-card-dialog-container',
				backdropClass: 'custom-backdrop',
				width: modalWidth,
				height: modalHeight,
				minWidth: modalMinWidth,
				maxWidth: '686px',
				maxHeight: `${MODAL_MAX_HEIGHT}px`,
				data: dialogData,
			})
			.afterClosed()
			.toPromise()
			.then((passes) => {
				if (passes && Array.isArray(passes) && !(passes[0] instanceof HallPass)) {
					const config: MatDialogConfig = {
						panelClass: 'student-pass-card-dialog-container',
						backdropClass: 'custom-backdrop',
						width: '40%',
						height: modalHeight,
						minWidth: '335px',
						maxWidth: '686px',
						maxHeight: '905px',
						data: {
							pass: passes[0],
							fromPast: false,
							forFuture: false,
							isActive: true,
							forStaff: false,
							kioskMode: false,
							activePassTime$: this.heartBeat,
							showStudentInfoBlock: false,
						},
					};
					this.dialog.open(PassCardComponent, config);
				}
			});
	}

	private hasActiveNowPassRequest(requests: Request[]): boolean {
		for (const request of requests) {
			if (request instanceof Request && request.status === 'pending' && request?.request_time === null) {
				return true;
			}
		}
		return false;
	}

	/**************
	 ********* Settings
	 **************/

	// FIXME: Remove this, bringing the settings-opening behavior into the navbar (or child) component
	@Output() settingsClick: EventEmitter<DialogData> = new EventEmitter<DialogData>();

	isOpenSettings = false;

	// FIXME: Bring this into child components
	areProfilePicturesEnabled = false;

	showOptions(event): void {
		if (!this.isOpenSettings) {
			const target = new ElementRef(event.currentTarget);
			if (!this.screenService.isDeviceLargeExtra) {
				this.isOpenSettings = true;
				UNANIMATED_CONTAINER.next(true);
				const settingRef = this.dialog.open(SettingsComponent, {
					panelClass: ['calendar-dialog-container', 'animation'],
					backdropClass: 'invis-backdrop',
					data: { trigger: target, adjustForScroll: true },
				});

				settingRef
					.afterClosed()
					.toPromise()
					.then((action) => {
						UNANIMATED_CONTAINER.next(false);
						this.isOpenSettings = false;
						this.settingsAction(action);
					});
			}

			this.settingsClick.emit({
				trigger: target,
			});

			this.sideNavService.sideNavData$.next({
				trigger: target,
			});

			this.sideNavService.sideNavType$.next('left');
		}
	}

	private settingsAction(action: SettingAction): void {
		switch (action) {
			case SettingAction.Appearance:
				this.dialog.open(SpAppearanceComponent, {
					panelClass: 'sp-form-dialog',
				});
				break;
			case SettingAction.Bug:
				window.open('https://www.smartpass.app/bugreport');
				break;
			case SettingAction.Classes:
				this.sideNavService.toggleLeft$.next(false);
				this.sideNavService.sideNavAction$.next(undefined);
				this.router.navigate(['main', 'classes']);
				break;
			case SettingAction.Favorite:
				{
					const favRef = this.dialog.open(FavoriteFormComponent, {
						panelClass: 'form-dialog-container',
						backdropClass: 'custom-backdrop',
						data: { isStaff: this.isStaff },
					});

					favRef
						.afterClosed()
						.toPromise()
						.then((data) => {
							this.locationService.updateFavoriteLocationsRequest(data);
						});
				}
				break;
			case SettingAction.Groups:
				this.dialog.open(GroupsSettingsComponent, {
					panelClass: 'app-group-settings-container',
				});
				break;
			case SettingAction.IdCard:
				this.openIDCard();
				break;
			case SettingAction.Language:
				this.dialog.open(SpLanguageComponent, {
					panelClass: 'sp-form-dialog',
				});
				break;
			case SettingAction.MyPin:
				this.dialog.open(TeacherPinComponent, {
					panelClass: 'sp-form-dialog',
					backdropClass: 'custom-backdrop',
				});
				break;
			case SettingAction.Notifications:
				if (!this.isSafari) {
					Notification.requestPermission();
				}

				if (NotificationService.hasSupport && NotificationService.canRequestPermission && !this.isSafari) {
					this.notifService.initNotifications(true).then(() => {
						this.dialog.open(NotificationFormComponent, {
							panelClass: 'form-dialog-container',
							backdropClass: 'custom-backdrop',
							width: '462px',
							height: '600px',
						});
					});
				} else {
					this.dialog.open(NotificationFormComponent, {
						panelClass: 'form-dialog-container',
						backdropClass: 'custom-backdrop',
						width: '462px',
						height: '600px',
					});
				}
				break;
			case SettingAction.Privacy:
				window.open('https://www.smartpass.app/privacy?new=true');
				break;
			case SettingAction.Profile:
				this.dialog.open(MyProfileDialogComponent, {
					panelClass: 'sp-form-dialog',
					width: '425px',
					height: '500px',
				});
				break;
			case SettingAction.Refer:
				if (this.introsData?.referral_reminder.universal && !this.introsData?.referral_reminder.universal.seen_version) {
					this.userService.updateIntrosRequest(this.introsData, 'universal', '1');
				}
				this.goToReferralPage();
				break;
			case SettingAction.ReferSchool:
				this.userService.updateIntrosShareSmartpassRequest(this.introsData, 'universal', '1');
				window.open(`https://refer.smartpass.app/v2/4/register`);
				break;
			case SettingAction.SignOut:
				this.isKioskMode = false;
				console.log('navigating to sign-out');
				this.router.navigate(['sign-out']);
				break;
			case SettingAction.Support:
				if (this.isStaff || this.isAssistant) {
					window.open('https://smartpass.app/support');
				} else {
					window.open('https://smartpass.app/studentdocs');
				}
				break;
			case SettingAction.SwagShop:
				window.open('https://shop.smartpass.app/');
				break;
			case SettingAction.Team:
				window.open('https://smartpass.app/team.html');
				break;
			case SettingAction.Term:
				window.open('https://www.smartpass.app/terms');
				break;
			case SettingAction.WishList:
				window.open('https://wishlist.smartpass.app');
				break;
		}
	}

	IDCARDDETAILS: IDCardDetails;

	async openIDCard(): Promise<void> {
		const idCardData: IDCard = {
			backgroundColor: this.IDCARDDETAILS.color,
			greadLevel: this.IDCARDDETAILS.show_grade_levels ? this.user.grade_level : null,
			idNumberData: this.user?.custom_id
				? {
						idNumber: this.user?.custom_id,
						barcodeURL: await this.qrBarcodeGenerator.selectBarcodeType(this.IDCARDDETAILS.barcode_type, this.user?.custom_id),
				  }
				: { idNumber: '', barcodeURL: '' },
			barcodeType: this.IDCARDDETAILS.barcode_type,
			backsideText: this.IDCARDDETAILS.backside_text,
			logoURL: this.IDCARDDETAILS.signed_url,
			profilePicture: this.user.profile_picture,
			schoolName: 'Demo School',
			userName: this.user.display_name,
			userRole: 'Student',
			showCustomID: this.IDCARDDETAILS.show_custom_ids,
			stickers: this.IDCARDDETAILS.stickers,
		};

		this.dialog.open(IdcardOverlayContainerComponent, {
			panelClass: 'id-card-overlay-container',
			backdropClass: 'custom-bd',
			data: { idCardData: idCardData, isLoggedIn: true },
		});
	}

	goToReferralPage(): void {
		this.router.navigate(['main', 'refer_us']);
	}

	/**************
	 ********* Authorization / Roles
	 **************/

	isStaff: boolean;
	isStudent: boolean;
	isAssistant: boolean;

	user: User;
	effectiveUser: User;

	/**************
	 ********* No Fly Times
	 **************/

	noFlyTimes: NoFlyTime[] = [];
	currentNoFlyTimeIndex: number;
	currentNoFlyEndTimeString: string;
	noFlyTimeActive = false;
	hasActivePassRequest: boolean;

	noFlyTimeTooltipPosition = [
		{
			originX: 'center',
			originY: 'bottom',
			overlayX: 'center',
			overlayY: 'top',
		},
	];
	isOpenStudentNoFlyTimeTooltip: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

	showStudentNoFlyTimeTooltip(): void {
		this.isOpenStudentNoFlyTimeTooltip.next(true);
	}

	closeStudentNoFlyTimeTooltip(): void {
		this.isOpenStudentNoFlyTimeTooltip.next(false);
	}

	/**************
	 ********* Inbox
	 **************/

	inboxVisibility: boolean = JSON.parse(this.storage.getItem('showInbox'));
	islargeDeviceWidth: boolean;

	/**************
	 ********* Referrals, Intros, and Streaks
	 **************/

	introsData: IntroData;
	referralEnabled = this.featureService.isFeatureEnabled(FLAGS.ReferralProgramme);
	isStreaks = this.featureService.isFeatureEnabled(FLAGS.ShowStreaks);

	/**************
	 ********* Kiosk and Parent Modes
	 **************/

	isKioskMode = this.kioskMode.isKioskMode();
	kioskModeLocation: Location;
	kioskModeRoomIcon: string;
	kioskModeRoomIconGradientColor: string;
	// commenting out kioskModeRoomPinnable until we re-introduce sp-pass-counter-chip
	// kioskModeRoomPinnable: Pinnable;
	hideButtons = this.router.url.includes('kioskMode') || this.router.url.includes('parent');
	passLimits: { [id: number]: PassLimit };

	listeningForActivePassCount = false;
	isKioskModeSettingsPage = false;

	wilUiRefresh = this.featureService.isFeatureEnabledV2(FLAGS.WaitInLineUIRefresh);
	isAttendanceEnabled = this.entitlementsService.isEntitlementEnabled(ENTITLEMENTS.Attendance);

	openKioskCheckIn(): void {
		this.dialog.open(KioskCheckInDialogComponent, {
			panelClass: 'sp-form-dialog',
			width: '425px',
			height: '500px',
			data: { currentLocation: this.kioskModeLocation },
		});
	}

	/**************
	 ********* Responsive Layout
	 **************/

	isHelpCenterOpen$ = this.helpCenter.isHelpCenterOpen;

	// UI indicators
	isMobile = DeviceDetection.isMobile() || (this.screenService.windowWidth < 1170 && this.helpCenter.isHelpCenterOpen.getValue());
	isIOSTablet = DeviceDetection.isIOSTablet();
	navBarBreakPoint = this.screenService.extraLargeDeviceBreakPoint;
	// FIXME: Why is this not using isMobile()?
	private isSmartphone = DeviceDetection.isAndroid() || DeviceDetection.isIOSMobile();

	private getMediaClass(): string {
		let rightClass = '';
		if (this.helpCenter.isHelpCenterOpen.getValue()) {
			if (this.screenService.windowWidth < 320) {
				rightClass = 'mediaWidth320';
			} else if (this.screenService.windowWidth < 475) {
				rightClass = 'mediaWidth475';
			} else if (this.screenService.windowWidth < 700) {
				rightClass = 'mediaWidth700';
			} else if (this.screenService.windowWidth < 940) {
				rightClass = 'mediaWidth940';
			}
		}
		return rightClass;
	}

	mediaClass = this.getMediaClass();

	/**************
	 ********* Notifications
	 **************/

	isSafari = DeviceDetection.isSafari();

	@HostListener('window:resize')
	checkDeviceWidth() {
		this.islargeDeviceWidth = this.screenService.isDeviceLargeExtra;

		if (this.islargeDeviceWidth) {
			this.inboxVisibility = false;
		}

		if (this.screenService.isDesktopWidth) {
			this.inboxVisibility = true;
		}
		this.dataService.updateInbox(this.inboxVisibility);
	}

	/**************
	 ********* Lifecycle Methods
	 **************/

	constructor(
		private dataService: DataService,
		private userService: UserService,
		private liveDataService: LiveDataService,
		public dialog: MatDialog,
		private router: Router,
		private locationService: LocationsService,
		private navbarData: NavbarDataService,
		private notifService: NotificationService,
		public darkTheme: DarkThemeSwitch,
		private http: HttpService,
		private storage: StorageService,
		private kioskMode: KioskModeService,
		public screenService: ScreenService,
		private sideNavService: SideNavService,
		private navbarElementsService: NavbarElementsRefsService,
		private shortcutsService: KeyboardShortcutsService,
		private qrBarcodeGenerator: QRBarcodeGeneratorService,
		private idCardService: IDCardService,
		public featureService: FeatureFlagService,
		private helpCenter: HelpCenterService,
		private hallPassService: HallPassesService,
		public emergencyService: EmergencyService,
		private noFlyTimeService: NoFlyTimeService,
		private scrollService: ScrollPositionService,
		private liveUpdateService: LiveUpdateService,
		private entitlementsService: EntitlementsService,
		public proTrialService: ProTrialService
	) {}

	ngOnInit(): void {
		timer(0, 1000)
			.pipe(takeUntil(this.destroyer$))
			.subscribe(() => {
				this.heartBeat.next(0);
			});

		combineLatest([this.noFlyTimeService.noFlyTimes$, this.noFlyTimeService.noFlyTimeEnabled])
			.pipe(takeUntil(this.destroyer$))
			.subscribe(([noFlyTimes, noFlyTimeEnabled]) => {
				this.noFlyTimes = noFlyTimes;
				this.currentNoFlyTimeIndex = this.noFlyTimeService.checkNoFlyTimes(this.noFlyTimes);
				if (this.currentNoFlyTimeIndex >= 0 && noFlyTimeEnabled) {
					this.noFlyTimeActive = true;
					const currentEndTime = this.noFlyTimeService.momentize(this.noFlyTimes[this.currentNoFlyTimeIndex].end_time);
					this.currentNoFlyEndTimeString = this.noFlyTimeService.formatTimeString(currentEndTime);
				} else {
					this.noFlyTimeActive = false;
				}
			});

		this.navbarData.createPassReceiver.pipe(takeUntil(this.destroyer$)).subscribe((state) => {
			state ? this.showMainFormForState(state) : this.showMainForm(false);
		});

		this.router.events.pipe(takeUntil(this.destroyer$)).subscribe((value) => {
			if (value instanceof NavigationEnd) {
				// safety to unsubscribe from listener if we navigate away from kiosk mode
				if (this.listeningForActivePassCount && !this.isKioskMode) {
					this.liveUpdateService.sendMessage('location.active_pass_counts.disable', null);
					this.listeningForActivePassCount = false;
				}
				// subscribe to origin counter listener if we navigate to kiosk mode
				if (this.isKioskMode) {
					this.liveUpdateService.isConnected$.pipe(takeUntil(this.destroyer$)).subscribe((isConnected) => {
						if (isConnected) {
							this.liveUpdateService.sendMessage('location.active_pass_counts.enable', null);
						}
					});
					this.locationService
						.listenPassLimitSocket()
						.pipe(takeUntil(this.destroyer$))
						.subscribe((loc) => {
							this.locationService.updatePassLimitRequest(loc);
						});
					this.listeningForActivePassCount = true;
				}
				if (value.url === `/main/kioskMode/settings`) {
					this.isKioskModeSettingsPage = true;
				}
				this.hideButtons = this.router.url.includes('kioskMode') || this.router.url.includes('parent');
			}
		});

		this.http.globalReload$
			.pipe(
				switchMap(() => {
					return this.userService.userJSON$.pipe(filter((u) => !!u));
				}),
				takeUntil(this.destroyer$),
				distinctUntilChanged((u1, u2) => u1.id === u2.id),
				tap((user) => {
					this.user = User.fromJSON(user);

					if (this.user.isStudent()) {
						// for some reason, using this.liveDataService.watchActivePassLike waits too long and the Observable value "flickers"
						// between true and false, weird
						// The following Observable doesn't have that issue, it's basically almost the same thing as watchActivePassLike
						combineLatest([
							this.liveDataService.watchActiveHallPasses(of({ sort: '-created', search_query: '' }), {
								type: 'student',
								value: this.user,
							}),
							this.liveDataService.watchActiveRequests(this.user),
							this.liveDataService.watchWaitingInLinePasses({ type: 'student', value: this.user }),
						])
							.pipe(
								map(([pass, request, wil]) => {
									this.createButtonTooltipText = this.hallPassService.getCreatePassButtonTooltipText(
										this.emergencyService.isEmergencyActivated.value,
										!!pass[0],
										this.hasActiveNowPassRequest(request),
										!!wil[0]
									);
									this.hasActivePassRequest = this.hasActiveNowPassRequest(request);
									return !!pass[0] || this.hasActiveNowPassRequest(request) || !!wil[0];
								}),
								takeUntil(this.destroyer$)
							)
							.subscribe((value) => {
								this.isCreatePassButtonDisabled$.next(value);
							});
					} else {
						// teachers should always be able to create a pass
						this.isCreatePassButtonDisabled$.next(false);
					}

					//need to add origin pass count check here for page refresh
					if (this.kioskMode.isKioskMode()) {
						this.liveUpdateService.isConnected$.pipe(takeUntil(this.destroyer$)).subscribe((isConnected) => {
							if (isConnected) {
								this.liveUpdateService.sendMessage('location.active_pass_counts.enable', null);
							}
						});
						this.locationService
							.listenPassLimitSocket()
							.pipe(takeUntil(this.destroyer$))
							.subscribe((loc) => {
								this.locationService.updatePassLimitRequest(loc);
							});
						this.listeningForActivePassCount = true;
					}
					this.isStaff = this.user.isTeacher();
					this.isStudent = this.user.isStudent();
					this.isAssistant = this.user.isAssistant();
					if (!this.isStudent) {
						this.navBarBreakPoint = this.screenService.navBarStaffBreakpoint;
					}
					if (this.userService.getFeatureFlagDigitalID() && !this.kioskMode.isKioskMode()) {
						this.idCardService
							.getIDCardDetails()
							.pipe(takeUntil(this.destroyer$))
							.subscribe({
								next: (result: IDCardResponse) => {
									if (result?.results?.digital_id_card?.enabled) {
										this.IDCARDDETAILS = result.results.digital_id_card;
									}
								},
							});
					}
					return;
				}),

				// The rest of this pipe chain is only concerned with kiosk mode
				// note that it filters for values, so including anything in the subscribe() will
				// be blocked if not in kiosk mode.
				switchMap(() => {
					return this.kioskMode.getCurrentRoom();
				}),
				tap((location) => {
					this.isKioskMode = !!location;
					this.loaded = true;
				}),
				filter((location) => !!location),
				tap((location) => {
					this.kioskModeLocation = location;
				}),
				switchMap(() => {
					return this.hallPassService.getPinnablesRequest();
				}),
				filter((pinnables) => !!pinnables),
				switchMap(() => {
					return this.hallPassService.getPinnable(this.kioskModeLocation);
				}),
				filter((pinnable) => !!pinnable),
				take(1)
			)
			.subscribe((pinnable: Pinnable) => {
				// this.kioskModeRoomPinnable = pinnable;
				this.kioskModeRoomIcon = pinnable.icon;
				this.kioskModeRoomIconGradientColor = pinnable.gradient_color;
			});

		this.sideNavService.sideNavAction.pipe(takeUntil(this.destroyer$)).subscribe((action) => {
			if (action) {
				this.settingsAction(action);
			}
		});

		this.sideNavService.openSettingsEvent$
			.pipe(
				filter((r) => !!r),
				takeUntil(this.destroyer$)
			)
			.subscribe(() => this.showOptions(this.settingsButton));

		this.islargeDeviceWidth = this.screenService.isDeviceLargeExtra;

		this.areProfilePicturesEnabled = this.userService.getUserSchool().profile_pictures_enabled;

		this.userService.introsData$
			.pipe(
				filter((res) => !!res),
				takeUntil(this.destroyer$)
			)
			.subscribe((data) => {
				this.introsData = data;
			});

		this.shortcutsService.onPressKeyEvent$
			.pipe(
				pluck('key'),
				takeUntil(this.destroyer$),
				filter(() => !this.isKioskMode), // The shortcut interferes with kiosk mode so disable it.
				filter((key) => key[0] === 'n' || key[0] === 'f')
			)
			.subscribe((key) => {
				if (key[0] === 'n') {
					this.showMainForm(false);
				} else if (key[0] === 'f') {
					this.showMainForm(true);
				}
			});
		this.locationService.getPassLimitRequest();
		this.locationService.pass_limits_entities$.pipe(takeUntil(this.destroyer$)).subscribe((res) => {
			this.passLimits = res;
		});
	}

	ngAfterViewInit(): void {
		this.navbarElementsService.navbarRef$.next(this.navbar);
	}

	ngOnDestroy(): void {
		this.liveUpdateService.sendMessage('location.active_pass_counts.disable', null);
		this.destroyer$.next(undefined);
		this.destroyer$.complete();
	}
}
